import { useState } from "react"
import * as React from "react"
import type {
  BillingAdjustmentType,
  ConsumerBillType,
  ConsumerDetailsType,
  dashboardOverviewType,
  // PaginationType,
  // PaymentAnalysisType,
  PaymentType,
  WeeklyPaymentsChartType,
} from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { formatDistanceToNow, nextMonday } from "date-fns"
import type { DateRange } from "react-day-picker"

import { meterDailyAnalyticsCountType } from "@/types/meter"
import { meterChartConfig } from "@/config/charts"
import { getLatestAnnouncement } from "@/lib/api/announcement"
import { canViewThisPage } from "@/lib/api/auth"
import {
  getConsumerDetails,
  getDashboardOverview,
  getPaymentAnalysis,
  getRecentBillAdjustment,
  getRecentConsumerBill,
  getRecentConsumerDetails,
  getRecentPayments,
  getWeeklyPayments,
} from "@/lib/api/billing"
import { getMeterDailyAnalyticsCount } from "@/lib/api/meter"
import { getStatusStyle } from "@/lib/statusStyles"
import { formatDateAndTime } from "@/lib/utils"
//Payment Analysis
// import { calculateDateRange, TimeSelection } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from "@/components/ui/credenza"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AreaChartComponent } from "@/components/charts/areachart"
import { BarChartStacked } from "@/components/charts/barchart-stacked"
import { PieChartComponent } from "@/components/charts/piechart"
import { ComboboxSearch } from "@/components/combobox-search"
import { RecentDataTabs } from "@/components/dashboard/recent-data-tabs-table"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
// import PaymentAnalysis from "@/components/payments/payment-analysis"
import SelectTimeRange, {
  timeRangeOptions,
} from "@/components/select-time-range"

export const Route = createFileRoute("/_home/dashboard")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_dashboard")) {
      return
    }

    return redirect({ to: "/access-denied" })
  },
  component: DashboardPage,
})

function DashboardPage() {
  const [timeRange, setTimeRange] = useState(timeRangeOptions()[0])
  const [activeTab, setActiveTab] = React.useState("Payments")
  const [activeRecentTab, setActiveRecentTab] =
    React.useState("Recent Billings")
  const [meterAnalyticsDate, setMeterAnalyticsDate] = React.useState<DateRange>(
    {
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    },
  )
  // Payment analysis
  // const [analysisType, setAnalysisType] = useState<TimeSelection>("daily")
  // const { startDate, endDate } = calculateDateRange(analysisType)

  const [showAllPayments, setShowAllPayments] = useState(false)
  const [showAllConsumers, setShowAllConsumers] = useState(false)
  const [showAllConsumerBilling, setShowAllConsumerBilling] = useState(false)
  const [showAllBillAdjustment, setShowAllBillAdjustment] = useState(false)
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
    to: new Date(), // Today
  })
  const {
    data: meterDailyAnalyticsCount,
    isRefetching: meterDailyAnalyticsCountLoading,
  } = useQuery<meterDailyAnalyticsCountType>({
    queryKey: [
      "meterDailyAnalyticsCount",
      meterAnalyticsDate.from?.toISOString(),
      meterAnalyticsDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = meterAnalyticsDate.from ?? new Date()
      const to = meterAnalyticsDate.to ?? new Date()
      return await getMeterDailyAnalyticsCount(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })
  const { data: dashboardOverview, isLoading: isDashboardOverviewLoading } =
    useQuery<dashboardOverviewType>({
      queryKey: ["dashboardOverview", timeRange.value],
      queryFn: () => getDashboardOverview(Number(timeRange.value)),
    })

  const { data: recentPayments, isLoading: isRecentPaymentsLoading } = useQuery<
    PaymentType[]
  >({
    queryKey: ["recentPayments"],
    queryFn: getRecentPayments,
  })
  const { data: recentConsumerBill, isLoading: isRecentConsumerBillLoading } =
    useQuery<ConsumerBillType[]>({
      queryKey: ["recentConsumerBill"],
      queryFn: getRecentConsumerBill,
    })
  const {
    data: recentBillAdjustment,
    isLoading: isRecentBillAdjustmentLoading,
  } = useQuery<BillingAdjustmentType[]>({
    queryKey: ["recentBillAdjustment"],
    queryFn: getRecentBillAdjustment,
  })
  const { data: consumerDetails, isLoading: isConsumerDetailsLoading } =
    useQuery<ConsumerDetailsType[]>({
      queryKey: ["recentConsumerDetails"],
      queryFn: getRecentConsumerDetails,
    })
  const { data: weeklyPayments, isLoading: isWeeklyPaymentsLoading } = useQuery<
    WeeklyPaymentsChartType[]
  >({
    queryKey: ["weeklyPayments", date],
    queryFn: () => getWeeklyPayments(date),
  })

  const { data: latestAnnouncement, isLoading } = useQuery({
    queryKey: ["latestAnnouncement"],
    queryFn: getLatestAnnouncement,
  })

  //Payment Analysis
  // const { data: paymentAnalysis, isLoading: isPaymentAnalysisLoading } =
  //   useQuery<PaymentAnalysisType[]>({
  //     queryKey: ["paymentAnalysis", analysisType],
  //     queryFn: () => {
  //       const { startDate, endDate } = calculateDateRange(analysisType)
  //       return getPaymentAnalysis(startDate, endDate)
  //     },
  //   })
  // console.log("paymentAnalysis>>>", paymentAnalysis)

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return formatDistanceToNow(date, { addSuffix: true })
  }

  const renderOverviewCard = (
    title: string,
    icon: keyof typeof Icons,
    data?: {
      total: number | string
      percentage: number | null
      status: string
    },
  ) => {
    const IconComponent = Icons[icon]
    const {
      color,
      bgColor,
      icon: StatusIcon,
    } = getStatusStyle(data?.status ?? "", data?.percentage ?? null)

    // compute displayTotal by parsing to Number and fixing 2 decimals
    const rawTotal = data?.total ?? 0;
    const displayTotal =
    title === "Total Payments"
    ? Number(rawTotal).toFixed(2)
    : rawTotal;

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex gap-2 text-[13px] font-medium text-muted-foreground">
            {["Total Consumers", "Active Consumers"].includes(title) && (
              <Icons.person className="h-[18px] w-[18px]" />
            )}
            {[
              "Total Payments",
              "Total Billing",
              "Total Bill Adjustment",
            ].includes(title) && (
              <img
                src="peso-sign.png"
                alt="icon"
                className="h-[18px] w-[18px]"
              />
            )}
            {["Total Meters"].includes(title) && (
              <Icons.gauge className="h-[20px] w-[20px] text-[#00B6AE]" />
            )}
            <p className="line-height-[14px] font-worksans font-bold tracking-wider">
              {title}
            </p>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-2xl font-bold">
          {isDashboardOverviewLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : data?.total !== undefined ? (
            <div className="flex flex-row items-center gap-2">
              <h1 className="font-semibold">
                {title === "Total Payments"
                  ? `₱ ${displayTotal}`
                  : displayTotal}
              </h1>
              {data.status !== "New" && (
                <span
                  className={`rounded-sm px-1 text-[13px] font-normal ${color} ${bgColor}`}
                >
                  {data.status !== "Flat" && data.percentage !== null
                    ? `${data.percentage.toFixed(2)}%`
                    : ""}

                  {StatusIcon && (
                    <span
                      className={
                        data.status === "Flat"
                          ? "flex items-center"
                          : "inline-block px-1"
                      }
                    >
                      {StatusIcon}
                    </span>
                  )}
                </span>
              )}
            </div>
          ) : (
            "N/A"
          )}
        </CardContent>
      </Card>
    )
  }
  const recentBillMockData = [
    {
      billing_id: "4114",
      consumer: "Donnell Tomagan",
      email: "dnnlltmgn1@icloud.com",
      contact_num: "+6309055125873",
      zone: "Zone 1",
      book: "Book A",
      meter_num: "MTR20250016",
      address:
        "Kanto Street Aplaya, CITY OF DIGOS (Capital) 8002, DAVAO DEL SUR, Davao Region PHL",
      acc_id: "DWD-400014",
      units: 21.0,
      date: "2026-04-15",
      due_date: "2026-05-17",
      due_amount: 210.0,
      total_amount: 210.0,
      file_path:
        "Billing System/Digos Water District/donnell_tomagan-apr_15_2026-may_15_2026.pdf",
    },
    {
      billing_id: "4113",
      consumer: "Donnell Tomagan",
      email: "dnnlltmgn1@icloud.com",
      contact_num: "+6309055125873",
      zone: "Zone 1",
      book: "Book A",
      meter_num: "MTR20250016",
      address:
        "Kanto Street Aplaya, CITY OF DIGOS (Capital) 8002, DAVAO DEL SUR, Davao Region PHL",
      acc_id: "DWD-400014",
      units: 21.0,
      date: "2026-04-15",
      due_date: "2026-05-17",
      due_amount: 210.0,
      total_amount: 210.0,
      file_path:
        "Billing System/Digos Water District/donnell_tomagan-apr_15_2026-may_15_2026.pdf",
    },
  ]
  const recentBillAdjustmentMockData = [
    {
      adjustment_id: 92,
      consumer_name: "Donnell Tomagan",
      billing_id: "4114",
      adjustment_reason: "test",
      previous_due_amount: 210.0,
      new_due_amount: 230.0,
      previous_consumption: 21.0,
      new_consumption: 23.0,
      adjustment_status: "PENDING",
      adjustment_date: "2025-04-07T01:21:13.349025",
      adjustment_notes: "test",
      requested_by: "Bryan Lim",
      date_approved_denied: "2025-04-07T13:04:47.515880",
      approved_denied_by: "Bryan Lim",
    },
    {
      adjustment_id: 93,
      consumer_name: "Bryan Lim",
      billing_id: "4115",
      adjustment_reason: "test",
      previous_due_amount: 250.0,
      new_due_amount: 200.0,
      previous_consumption: 21.0,
      new_consumption: 23.0,
      adjustment_status: "APPROVED",
      adjustment_date: "2025-05-07T01:21:13.349025",
      adjustment_notes: "test",
      requested_by: "Bryan Lim",
      date_approved_denied: "2025-03-07T13:04:47.515880",
      approved_denied_by: "Donnel",
    },
    {
      adjustment_id: 93,
      consumer_name: "Bryan Lim",
      billing_id: "4115",
      adjustment_reason: "test",
      previous_due_amount: 250.0,
      new_due_amount: 200.0,
      previous_consumption: 21.0,
      new_consumption: 23.0,
      adjustment_status: "DENIED",
      adjustment_date: "2025-05-07T01:21:13.349025",
      adjustment_notes: "test",
      requested_by: "Bryan Lim",
      date_approved_denied: "2025-03-07T13:04:47.515880",
      approved_denied_by: "Donnel",
    },
  ]

  // const renderPaymentsTable = () => {
  //   if (isRecentPaymentsLoading) {
  //     return (
  //       <Table>
  //         <TableBody>
  //           {[...Array(5)].map((_, i) => (
  //             <TableRow key={i}>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-32" />
  //               </TableCell>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-20" />
  //               </TableCell>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-24" />
  //               </TableCell>
  //             </TableRow>
  //           ))}
  //         </TableBody>
  //       </Table>
  //     )
  //   }

  //   return recentPayments && recentPayments.length > 0 ? (
  //     <Table>
  //       <TableHeader>
  //         <TableRow>
  //           <TableHead>Consumer</TableHead>
  //           <TableHead>Amount</TableHead>
  //           <TableHead>Date</TableHead>
  //         </TableRow>
  //       </TableHeader>
  //       <TableBody>
  //         {recentPayments.slice(0, showAllPayments ? 10 : 5).map((payment) => (
  //           <TableRow key={payment.payment_id}>
  //             <TableCell>{payment.consumer_name}</TableCell>
  //             <TableCell>₱{payment.amount_paid.toFixed(2)}</TableCell>
  //             <TableCell>{formatDate(payment.payment_date)}</TableCell>
  //           </TableRow>
  //         ))}
  //       </TableBody>
  //     </Table>
  //   ) : (
  //     <TableRow>
  //       <TableCell colSpan={3} className="text-center">
  //         No recent payments available
  //       </TableCell>
  //     </TableRow>
  //   )
  // }

  // const renderConsumerTable = () => {
  //   if (isConsumerDetailsLoading) {
  //     return (
  //       <Table>
  //         <TableBody>
  //           {[...Array(5)].map((_, i) => (
  //             <TableRow key={i}>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-16" />
  //               </TableCell>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-32" />
  //               </TableCell>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-64" />
  //               </TableCell>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-20" />
  //               </TableCell>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-20" />
  //               </TableCell>
  //               <TableCell>
  //                 <Skeleton className="h-4 w-24" />
  //               </TableCell>
  //             </TableRow>
  //           ))}
  //         </TableBody>
  //       </Table>
  //     )
  //   }

  //   if (!consumerDetails || consumerDetails.length === 0) {
  //     return (
  //       <TableRow>
  //         <TableCell colSpan={5} className="text-center">
  //           No consumer data available
  //         </TableCell>
  //       </TableRow>
  //     )
  //   }

  //   return (
  //     <Table>
  //       <TableHeader>
  //         <TableRow>
  //           <TableHead>ID</TableHead>
  //           <TableHead>Name</TableHead>
  //           <TableHead className="text-center">Address</TableHead>
  //           <TableHead>Acc No.</TableHead>
  //           <TableHead>Meter No.</TableHead>
  //           <TableHead>Contact No.</TableHead>
  //         </TableRow>
  //       </TableHeader>
  //       <TableBody>
  //         {consumerDetails
  //           .slice(0, showAllConsumers ? 10 : 5)
  //           .map((consumer) => (
  //             <TableRow key={consumer.consumer_id}>
  //               <TableCell>{consumer.consumer_id}</TableCell>
  //               <TableCell>{consumer.name}</TableCell>
  //               <TableCell>
  //                 <div
  //                   className="max-h-[70px] w-[420px] max-w-[420px] overflow-auto text-right md:max-w-full md:py-0 md:text-left"
  //                   title={consumer.address}
  //                 >
  //                   {consumer.address}
  //                 </div>
  //               </TableCell>
  //               <TableCell>{consumer.acc_id}</TableCell>
  //               <TableCell>{consumer.meter_num}</TableCell>
  //               <TableCell>{consumer.contact_num}</TableCell>
  //             </TableRow>
  //           ))}
  //       </TableBody>
  //     </Table>
  //   )
  // }

  return (
    <main className="space-y-4 p-4">
      <div className="flex w-full justify-between pt-4">
        <div>
          <Header>Dashboard</Header>
        </div>
        <div className="flex items-center gap-3">
          <Label>Set view</Label>
          <SelectTimeRange range={timeRange} setRange={setTimeRange} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:items-stretch">
        <div className="grid-cols-6 gap-4 space-y-4 overflow-visible font-worksans md:col-span-3 md:grid md:space-y-0">
          {renderOverviewCard(
            "Total Billing",
            "peso",
            dashboardOverview?.bills,
          )}
          {renderOverviewCard(
            "Total Bill Adjustment",
            "peso",
            dashboardOverview?.bill_adjustments,
          )}
          {renderOverviewCard(
            "Total Consumers",
            "user",
            dashboardOverview?.consumers,
          )}
          {renderOverviewCard(
            "Active Consumers",
            "user",
            dashboardOverview?.active_consumers,
          )}
          {renderOverviewCard(
            "Total Meters",
            "user",
            dashboardOverview?.meters,
          )}
          {renderOverviewCard(
            "Total Payments",
            "peso",
            dashboardOverview?.payments,
          )}
        </div>
        {/* <div>
          <PaymentAnalysis
            title="Payment Analysis"
            analysisType={analysisType}
            onAnalysisTypeChange={(val) =>
              setAnalysisType(val as TimeSelection)
            }
            isLoading={isPaymentAnalysisLoading}
            chartComponent={
              <PieChartComponent
                data={paymentAnalysis ?? []}
                dataKey="count"
                nameKey="name"
              />
            }
          />
        </div> */}
      </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr] md:items-stretch">
        
        {/* 1) Analytics Section */}
        <div className="flex h-full flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
            <div className="overflow-x-auto">
              <TabsList className="flex h-12 w-max items-center gap-0 rounded-lg border-2 bg-white">
                <TabsTrigger value="Payments" className="min-w-[120px] px-4 font-semibold transition-all data-[state=active]:bg-primary data-[state=active]:text-white">
                  Payment Analytics
                </TabsTrigger>
                <TabsTrigger value="Meter Analytics" className="min-w-[120px] px-4 font-semibold transition-all data-[state=active]:bg-primary data-[state=active]:text-white">
                  Meter Analytics
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="Payments" className="flex-1">
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-gilroy font-bold">Payment Analytics</CardTitle>
                    <DateRangePicker date={date} setDate={setDate} />
                  </div>
                </CardHeader>
                <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
                  {isWeeklyPaymentsLoading
                    ? <Skeleton className="h-full w-full" />
                    : <AreaChartComponent data={weeklyPayments} />}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="Meter Analytics" className="flex-1">
              <Card className="h-full">
                <CardHeader className="flex items-center justify-between">
                  <CardTitle>Meter Analytics</CardTitle>
                  <DateRangePicker date={meterAnalyticsDate} setDate={setMeterAnalyticsDate} />
                </CardHeader>
                <CardContent className="h-[calc(100%-57px)]">
                  {meterDailyAnalyticsCountLoading
                    ? <Skeleton className="h-full w-full" />
                    : <BarChartStacked data={meterDailyAnalyticsCount} config={meterChartConfig} />}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* 2) Announcement Section */}
        <div className="flex h-full flex-col border-l border-gray-200 pl-4 overflow-hidden">
          <Card className="flex flex-col h-full w-full">
            <CardHeader>
              <CardTitle className="text-2xl font-medium">
                <div className="flex items-center gap-2">
                  <Icons.partyPopper className="text-primary" />
                  Latest Announcement
                  <Icons.partyPopper className="text-primary" />
                </div>
              </CardTitle>
              <CardDescription className="py-2">
                <span className="block text-3xl font-medium text-muted-foreground">
                  {latestAnnouncement?.subject}
                </span>
                <span className="block text-sm font-bold text-muted-foreground">
                  {`${latestAnnouncement?.created_by} | ${formatDateAndTime(latestAnnouncement?.created_at)}`}
                </span>
              </CardDescription>
            </CardHeader>

            <CardContent className="h-[calc(100%-150px)] overflow-y-auto">
              <CardDescription className="line-clamp-6 text-lg">
                <strong className="text-lg">{latestAnnouncement?.header}</strong><br/><br/>
                {latestAnnouncement?.body}
              </CardDescription>
            </CardContent>

            <CardFooter className="p-4">
              <Credenza>
              <CredenzaTrigger asChild>
                <span
                  className="
                    inline-flex 
                    items-center 
                    whitespace-nowrap 
                    underline 
                    text-primary 
                    cursor-pointer
                  "
                >
                  Read more
                  <Icons.doubleRight className="ml-1" />
                </span>
              </CredenzaTrigger>
                <CredenzaContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                    <CredenzaHeader>
                      <CredenzaTitle className="text-2xl font-medium">
                        <div className="flex items-center justify-center gap-2">
                          <Icons.partyPopper className="text-primary" />
                          Latest Announcement
                          <Icons.partyPopper className="text-primary" />
                        </div>
                      </CredenzaTitle>
                      <CredenzaDescription className="flex flex-col py-2">
                        <span className="block text-xl font-semibold">
                          {latestAnnouncement?.subject}
                        </span>
                        <span className="block text-sm text-muted-foreground">
                          {`${latestAnnouncement?.created_by} | ${formatDateAndTime(latestAnnouncement?.created_at)}`}
                        </span>
                      </CredenzaDescription>
                    </CredenzaHeader>

                    {/* ─── FULL ANNOUNCEMENT CONTENT ─── */}
                    <div className="p-4 space-y-4">
                      {/* Announcement Header */}
                      <p className="text-lg font-medium">
                        {latestAnnouncement?.header}
                      </p>

                      {/* Announcement Body */}
                      <p className="whitespace-pre-line leading-relaxed">
                        {latestAnnouncement?.body}
                      </p>
                    </div>
                </CredenzaContent>
              </Credenza>
            </CardFooter>
          </Card>
        </div>
      </div>
      <div className="grid grid-cols-1 md:items-stretch">
        <RecentDataTabs
          activeTab={activeRecentTab}
          setActiveTab={setActiveRecentTab}
          showAllPayments={showAllPayments}
          setShowAllPayments={setShowAllPayments}
          showAllConsumers={showAllConsumers}
          setShowAllConsumers={setShowAllConsumers}
          showAllConsumerBilling={showAllConsumerBilling}
          setShowAllConsumerBilling={setShowAllConsumerBilling}
          showAllBillAdjustment={showAllBillAdjustment}
          setShowAllBillAdjustment={setShowAllBillAdjustment}
          recentPayments={recentPayments}
          isRecentPaymentsLoading={isRecentPaymentsLoading}
          consumerDetails={consumerDetails}
          isConsumerDetailsLoading={isConsumerDetailsLoading}
          recentConsumerBill={recentConsumerBill}
          isRecentConsumerBillLoading={isRecentConsumerBillLoading}
          recentBillAdjustment={recentBillAdjustment}
          isRecentBillAdjustmentLoading={isRecentBillAdjustmentLoading}
          formatDate={formatDate}
        />
        {/* <div className="flex h-full flex-col">
          <Tabs
            value={activeRecentTab}
            onValueChange={setActiveRecentTab}
            className="h-full"
          >
            <div className="overflow-x-auto p-0">
              <TabsList className="m-0 flex h-12 w-max items-center overflow-hidden rounded-lg border-2 bg-white p-0">
                <TabsTrigger
                  value="Recent Billings"
                  className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
                >
                  Recent Billings
                </TabsTrigger>
                <TabsTrigger
                  value="Recently Added Consumers"
                  className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
                >
                  Recently Added Consumers
                </TabsTrigger>
                <TabsTrigger
                  value="Recent Bill Adjustments"
                  className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
                >
                  Recent Bill Adjustments
                </TabsTrigger>
                <TabsTrigger
                  value="Recent Payments"
                  className="flex h-full min-w-[120px] items-center justify-center rounded-md px-4 font-semibold shadow-none transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=inactive]:text-gray-700"
                >
                  Recent Payments
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="Recent Billings" className="min-h-0 flex-1">
              <Card className="h-full">
                <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
                  <div className="flex items-center justify-end py-2">
                    {consumerDetails && consumerDetails.length > 5 && (
                      <Button
                        className="mt-0"
                        onClick={() => setShowAllConsumers(!showAllConsumers)}
                        // variant="outline"
                      >
                        {showAllConsumers ? "See Less" : "See More"}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">{renderConsumerTable()}</div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent
              value="Recently Added Consumers"
              className="min-h-0 flex-1"
            >
              <Card className="h-full">
                <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
                  <div className="flex items-center justify-end py-2">
                    {consumerDetails && consumerDetails.length > 5 && (
                      <Button
                        className="mt-0"
                        onClick={() => setShowAllConsumers(!showAllConsumers)}
                        // variant="outline"
                      >
                        {showAllConsumers ? "See Less" : "See More"}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">{renderConsumerTable()}</div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent
              value="Recent Bill Adjustments"
              className="min-h-0 flex-1"
            >
              <Card className="h-full">
                <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
                  <div className="flex items-center justify-end py-2">
                    {consumerDetails && consumerDetails.length > 5 && (
                      <Button
                        className="mt-0"
                        onClick={() => setShowAllConsumers(!showAllConsumers)}
                        // variant="outline"
                      >
                        {showAllConsumers ? "See Less" : "See More"}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">{renderConsumerTable()}</div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="Recent Payments" className="min-h-0 flex-1">
              <Card className="h-full">
                <CardContent className="h-[calc(100%-57px)] pl-2 pr-10">
                  <div className="flex items-center justify-end py-2">
                    {recentPayments && recentPayments.length > 5 && (
                      <Button
                        className="mt-0"
                        onClick={() => setShowAllPayments(!showAllPayments)}
                        // variant="outline"
                      >
                        {showAllPayments ? "See Less" : "See More"}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">{renderPaymentsTable()}</div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div> */}
      </div>
      {/* <div className="grid grid-cols-1 gap-2 md:grid-cols-4 md:items-stretch">
       

        <Card className="col-span-1 py-3 sm:col-span-3 md:col-span-3 lg:col-span-1">
          <CardContent>
            <div className="flex items-center justify-between">
              <h1 className="py-3 text-xl font-semibold">Recent Payments</h1>
              {recentPayments && recentPayments.length > 5 && (
                <Button
                  className="mt-0"
                  onClick={() => setShowAllPayments(!showAllPayments)}
                  variant="outline"
                >
                  {showAllPayments ? "See Less" : "See More"}
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">{renderPaymentsTable()}</div>
          </CardContent>
        </Card>
      </div> */}
    </main>
  )
}
